import { useEffect, useState } from "react";
import { DoorOpen, RotateCcw, UserRoundCheck } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { todayET } from "@/lib/dates";

export const END_REASONS = [
  ["leave", "Leave"],
  ["resignation", "Resignation"],
  ["termination", "Termination"],
  ["reassignment", "Reassignment"],
  ["contract_end", "Contract end"],
  ["duplicate_record", "Duplicate record"],
];

const today = () => todayET();

function EndedSummary({ assignment, onRestart, busy }) {
  const ending = assignment.ending;
  const [startDate, setStartDate] = useState(today());
  const [note, setNote] = useState("");
  return (
    <div className="assignment-ending-summary" data-testid="assignment-ended-summary">
      <p>
        <strong>Ended {ending.end_date}</strong> · {ending.reason_label}
        {ending.note ? ` · ${ending.note}` : ""} · by {ending.ended_by_name} {ending.ended_at?.slice(0, 10)}
      </p>
      {ending.covering_provider_name && (
        <p data-testid="assignment-ending-handoff">
          <UserRoundCheck size={13} /> Covered by {ending.covering_provider_name}: {ending.handoff_count} item{ending.handoff_count === 1 ? "" : "s"} handed off
          {ending.handoff_students?.length ? ` (students ${ending.handoff_students.join(", ")})` : ""}.
        </p>
      )}
      <p className="pairing-meta">{ending.reopen_position ? "Position reopened as an open vacancy." : "Position was not reopened."} Attendance alerts and time exceptions stop after the end date; the client team page drops this placement on that date.</p>
      <form className="assignment-ending-form" onSubmit={(e) => { e.preventDefault(); onRestart({ start_date: startDate, note }); }}>
        <label>New start date<input data-testid="assignment-restart-date" type="date" required value={startDate} onChange={(e) => setStartDate(e.target.value)} /></label>
        <label className="assignment-ending-wide">Note (optional)<input data-testid="assignment-restart-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder="Why the placement is restarting" /></label>
        <button className="button button-secondary" data-testid="assignment-restart-button" type="submit" disabled={busy}><RotateCcw size={14} /> Restart assignment</button>
      </form>
    </div>
  );
}

export function EndAssignmentPanel({ assignment, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [providers, setProviders] = useState([]);
  const [form, setForm] = useState({ end_date: today(), reason: "", note: "", covering_provider_id: "", reopen_position: false });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    setOpen(false);
    setError("");
    setForm({ end_date: today(), reason: "", note: "", covering_provider_id: "", reopen_position: false });
  }, [assignment.id]);

  useEffect(() => {
    if (!open || providers.length) return;
    api.get("/providers/approved").then((r) => setProviders(r.data)).catch((e) => setError(formatApiError(e)));
  }, [open, providers.length]);

  async function submit(path, payload) {
    try {
      setBusy(true);
      setError("");
      const response = await api.post(`/assignments/${assignment.id}/${path}`, payload);
      onUpdated(response.data);
      setOpen(false);
    } catch (requestError) {
      setError(formatApiError(requestError));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="assignment-status-panel assignment-ending-panel" data-testid="assignment-ending-panel">
      <div className="section-heading">
        <div>
          <h3><DoorOpen size={17} /> {assignment.status === "Ended" ? "Assignment ended" : "End assignment"}</h3>
          <p>{assignment.status === "Ended" ? "Restart restores the placement with a new start date." : "Works from any status, even with pre-start clearances missing. The position stays filled unless you choose to reopen it."}</p>
        </div>
        {assignment.status !== "Ended" && !open && <button className="button button-secondary" data-testid="assignment-end-button" type="button" onClick={() => setOpen(true)}><DoorOpen size={14} /> End assignment</button>}
      </div>
      {error && <p className="form-message error-message" data-testid="assignment-ending-error">{error}</p>}
      {assignment.status === "Ended" && <EndedSummary assignment={assignment} busy={busy} onRestart={(payload) => submit("restart", payload)} />}
      {assignment.status !== "Ended" && open && (
        <form className="assignment-ending-form" data-testid="assignment-end-form" onSubmit={(e) => { e.preventDefault(); submit("end", { ...form, covering_provider_id: form.covering_provider_id || null }); }}>
          <label>End date<input data-testid="assignment-end-date" type="date" required value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></label>
          <label>Reason<select data-testid="assignment-end-reason" required value={form.reason} onChange={(e) => setForm({ ...form, reason: e.target.value })}><option value="">Choose…</option>{END_REASONS.map(([value, label]) => <option key={value} value={value}>{label}</option>)}</select></label>
          <label>Covering provider (optional)<select data-testid="assignment-end-covering" value={form.covering_provider_id} onChange={(e) => setForm({ ...form, covering_provider_id: e.target.value })}><option value="">No covering provider</option>{providers.filter((p) => p.id !== assignment.provider_id).map((p) => <option key={p.id} value={p.id}>{p.first_name} {p.last_name}{p.discipline ? ` · ${p.discipline}` : ""}</option>)}</select></label>
          <label className="assignment-ending-wide">Note (optional)<input data-testid="assignment-end-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} placeholder="Context for the timeline" /></label>
          {assignment.request_id && !assignment.record_keeping && (
            <label className="assignment-ending-check assignment-ending-wide"><input data-testid="assignment-end-reopen" type="checkbox" checked={form.reopen_position} onChange={(e) => setForm({ ...form, reopen_position: e.target.checked })} /> Reopen position as an open vacancy</label>
          )}
          {form.covering_provider_id && <p className="pairing-meta assignment-ending-wide" data-testid="assignment-end-handoff-note">Open evaluations (with their students), client tasks, placements and tasks for {assignment.client_name} move to the covering provider; the handoff is logged on each record and shown in their portal.</p>}
          <div className="button-row assignment-ending-wide">
            <button className="button button-primary" data-testid="assignment-end-submit" type="submit" disabled={busy || !form.reason}>{busy ? "Ending…" : "End assignment"}</button>
            <button className="provider-profile-link" data-testid="assignment-end-cancel" type="button" onClick={() => setOpen(false)}>Cancel</button>
          </div>
        </form>
      )}
    </section>
  );
}
